if (!customElements.get("nearby-locations")) {
  customElements.define(
    "nearby-locations",
    class NearbyLocations extends HTMLElement {
      constructor() {
        super()

        this.locationsContainer = this.querySelector(".nearby-locations__list")

        this.locations = []
        this.currentProductId = null
        this.isLoading = false

        this.init()
      }

      connectedCallback() {
        this.init()
        this.setupEventListeners()
      }

      disconnectedCallback() {
        if (this.bevmoLocationUpdateUnsubscriber) {
          this.bevmoLocationUpdateUnsubscriber()
        }
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber()
        }
        if (this.quantityUpdateUnsubscriber) {
          this.quantityUpdateUnsubscriber()
        }
      }

      setupEventListeners() {
        // Subscribe to bevmoLocationUpdate event
        this.bevmoLocationUpdateUnsubscriber = subscribe(
          PUB_SUB_EVENTS.bevmoLocationUpdate,
          this.onLocationUpdate.bind(this)
        )

        // Subscribe to cartUpdate event to handle cart quantity changes
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this))

        // Subscribe to quantityUpdate event to re-evaluate when stock is updated
        this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.onQuantityUpdate.bind(this))
      }

      onLocationUpdate(data) {
        // Hide component by default
        this.style.display = "none"

        // Clear old locations to prevent stale data from being displayed
        this.locations = []

        // Re-check if component should be visible based on new fulfillment type
        if (!this.shouldShowComponent()) {
          return
        }

        // Reload nearby locations with new coordinates
        // Note: We'll check stock in renderLocations(), but if stock hasn't updated yet,
        // we'll be permissive and show the component. onQuantityUpdate() will re-evaluate
        // when stock is updated to ensure correct visibility.
        this.loadNearbyLocations()
      }

      onQuantityUpdate() {
        // Don't re-render if we're currently loading new locations
        // This prevents displaying stale locations during location change
        if (this.isLoading) {
          return
        }

        // Re-evaluate component visibility when stock is updated
        // This handles the race condition where stock updates after location change
        // If we have locations loaded, re-render to check stock status
        if (this.locations && this.locations.length > 0) {
          this.renderLocations()
        } else if (this.shouldShowComponent() && this.shouldShowComponentForCart(window.cart)) {
          // If no locations loaded yet but component should be visible, try loading
          this.loadNearbyLocations()
        }
      }

      onCartUpdate(data) {
        // Hide component by default
        this.style.display = "none"

        // Check if component should be visible based on cart quantity
        if (!this.shouldShowComponentForCart(data.cart) || !this.shouldShowComponent()) {
          return
        }

        // Reload locations to check if any are available
        this.loadNearbyLocations()
      }

      init() {
        // Hide component by default
        this.style.display = "none"

        // Only show component if fulfillment type is pickup
        if (!this.shouldShowComponent()) {
          return
        }

        // Get product IDs from various sources
        this.currentProductIds = this.getAllProductIds()
        this.currentProductId = this.currentProductIds[0] || null // Keep for backward compatibility

        // Load nearby locations if data is available
        this.loadNearbyLocations()
      }

      shouldShowComponent() {
        // Only show when bevmoLocation.fulfillment_type is 'pickup'
        return window.bevmoLocation?.fulfillment_type === "pickup"
      }

      shouldShowComponentForCart(cart) {
        // Only show when cart quantity for this product is 0 or product quantity is 0
        const quantityPill = document.querySelector("product-info quantity-pill")
        if (!cart || !cart.items || (quantityPill && quantityPill.max === 0)) {
          return true // Show if no cart data available
        }

        const productIds = this.getAllProductIds()

        // Check if any of the current product IDs are in the cart
        const hasProductInCart = cart.items.some((item) => {
          const itemSku = item.sku || item.product_id?.toString()
          return productIds.includes(itemSku)
        })

        // Only show component when product is NOT in cart (cart_qty = 0)
        return !hasProductInCart
      }

      async loadNearbyLocations() {
        try {
          // Set loading flag to prevent stale data from being displayed
          this.isLoading = true

          // Double-check fulfillment type before loading
          if (!this.shouldShowComponent()) {
            this.style.display = "none"
            this.isLoading = false
            return
          }

          this.showLoading()

          // Get coordinates from bevmoLocation
          if (!window.bevmoLocation?.place?.geometry?.location) {
            console.warn("No location coordinates available")
            this.isLoading = false
            this.renderEmptyState()
            return
          }

          const { lat, lng } = window.bevmoLocation.place.geometry.location
          const productIds = this.getAllProductIds().join(",")

          if (!productIds) {
            console.warn("No product IDs available")
            this.isLoading = false
            this.renderEmptyState()
            return
          }

          // Get total miles from data attribute
          const totalMiles = this.dataset.totalMiles || 10

          // Fetch location data from API
          const apiUrl = `${window.gopuffShopify.settings.api_base_url}/shops/locations-with-products-by-coordinates?latitude=${lat}&longitude=${lng}&product_ids=${productIds}&miles=${totalMiles}`

          const response = await fetch(apiUrl)
          if (!response.ok) {
            throw new Error(`API request failed: ${response.status}`)
          }

          const data = await response.json()

          if (data.locations && Array.isArray(data.locations)) {
            this.locations = data.locations
            this.isLoading = false
            this.renderLocations()
          } else {
            this.isLoading = false
            this.renderEmptyState()
          }
        } catch (error) {
          console.error("Error loading location data:", error)
          this.isLoading = false
          this.renderErrorState()
        }
      }

      showLoading() {
        if (this.locationsContainer) {
          this.locationsContainer.innerHTML = `
            <div class="nearby-locations__loading">
              <p>Loading nearby locations...</p>
            </div>
          `
        }
      }

      renderErrorState() {
        if (this.locationsContainer) {
          this.locationsContainer.innerHTML = `
            <div class="nearby-locations__error">
              <p>Error loading nearby locations. Please try again later.</p>
            </div>
          `
        }
      }

      setLocations(locations) {
        this.locations = locations || []
        this.renderLocations()
      }

      renderLocations() {
        if (!this.locationsContainer) {
          console.warn("Nearby locations container not found")
          return
        }

        // Clear existing content
        this.locationsContainer.innerHTML = ""

        if (!this.locations || this.locations.length === 0) {
          this.style.display = "none"
          return
        }

        // Filter locations that have the current product available
        let availableLocations = this.filterLocationsByProduct(this.locations)

        // Filter out the currently selected location
        const selectedLocationId = window.bevmoLocation?.location_id
        if (selectedLocationId) {
          availableLocations = availableLocations.filter((location) => {
            return String(location.id) !== String(selectedLocationId)
          })
        }

        if (availableLocations.length === 0) {
          this.style.display = "none"
          return
        }

        // Don't show if product is in stock at current location
        // Only hide if we can definitively determine that stock > 0
        // If quantityPill.max is null/undefined, it may not have updated yet, so be permissive
        const quantityPill = document.querySelector("product-info quantity-pill")
        if (quantityPill && typeof quantityPill.max === 'number' && quantityPill.max > 0) {
          // Product is definitely in stock, hide component
          this.style.display = "none"
          return
        }
        // If quantityPill.max is null/undefined or 0, show component
        // onQuantityUpdate() will re-evaluate when stock is updated

        // Show component since we have available locations
        this.style.display = ""

        // Render each location
        availableLocations.forEach((location) => {
          const locationElement = this.createLocationElement(location)
          this.locationsContainer.appendChild(locationElement)
        })
      }

      filterLocationsByProduct(locations) {
        if (!this.currentProductIds || this.currentProductIds.length === 0) {
          return locations
        }

        return locations.filter((location) => {
          return (
            location.available_products &&
            location.available_products.some(
              (product) => this.currentProductIds.includes(product.id.toString()) && product.quantity > 0
            )
          )
        })
      }

      createLocationElement(location) {
        const locationDiv = document.createElement("div")
        locationDiv.className = "nearby-locations__location"

        // Get product quantity for this location
        const productInfo = this.getProductInfoForLocation(location)
        const quantity = productInfo ? productInfo.quantity : 0

        locationDiv.innerHTML = `
          <div class="nearby-locations__location-content">
            <div class="nearby-locations__location-header">
              <div class="nearby-locations__location-info">
                <div class="nearby-locations__location-city-state">${this.escapeHtml(
                  location.address.city
                )}, ${this.escapeHtml(location.address.state)} ${this.escapeHtml(location.address.zip)}</div>
                <div class="nearby-locations__location-address">
                  <div class="nearby-locations__location-address1">${this.escapeHtml(location.address.address)}</div>
                </div>
              </div>
              <div class="nearby-locations__location-distance-actions">
                <span class="nearby-locations__location-distance">${this.formatDistance(location.distance_km)}</span>
                <button class="nearby-locations__switch-store-btn button" data-location-id="${
                  location.id
                }" data-location-data='${JSON.stringify(location).replace(/'/g, "&apos;")}'>
                  Switch Store
                </button>
              </div>
            </div>
          </div>
        `

        // Add event listener for switch store button
        const switchStoreBtn = locationDiv.querySelector(".nearby-locations__switch-store-btn")
        if (switchStoreBtn) {
          switchStoreBtn.addEventListener("click", this.handleSwitchStore.bind(this))
        }

        return locationDiv
      }

      getProductInfoForLocation(location) {
        if (!this.currentProductIds || this.currentProductIds.length === 0 || !location.available_products) {
          return null
        }

        // Find the first available product from our current product IDs
        for (const productId of this.currentProductIds) {
          const productInfo = location.available_products.find((product) => product.id.toString() === productId)
          if (productInfo && productInfo.quantity > 0) {
            return productInfo
          }
        }

        return null
      }

      async handleSwitchStore(event) {
        event.preventDefault()

        const button = event.currentTarget
        // Ensure locationId is a number (dataset always returns strings)
        const locationId = parseInt(button.dataset.locationId, 10)
        const locationDataStr = button.dataset.locationData

        try {
          const locationData = JSON.parse(locationDataStr.replace(/&apos;/g, "'"))

          // Construct location object for setLocation
          const locationObj = {
            address: {
              address1: locationData.address.address,
              city: locationData.address.city,
              province: locationData.address.state,
              postalCode: locationData.address.zip, // Use postalCode instead of postcode
              latitude: locationData.address.latitude, // Use latitude instead of lat
              longitude: locationData.address.longitude, // Use longitude instead of lng
            },
            fulfillment_type: "pickup",
            fulfillment_display_name: `${locationData.address.city}, ${this.convertStateToAbbreviation(
              locationData.address.state
            )}`,
            location_id: locationId,
            place: {
              place_id: locationData.address.google_place_id,
              formatted_address: `${locationData.address.address}${locationData.address.apt ? ', ' + locationData.address.apt : ''}, ${locationData.address.city}, ${this.convertStateToAbbreviation(locationData.address.state)} ${locationData.address.zip}`,
              geometry: {
                location: {
                  lat: locationData.address.latitude,
                  lng: locationData.address.longitude
                }
              },
              address_components: [
                {
                  long_name: locationData.address.address,
                  short_name: locationData.address.address,
                  types: ["street_number", "route"]
                },
                {
                  long_name: locationData.address.city,
                  short_name: locationData.address.city,
                  types: ["locality"]
                },
                {
                  long_name: locationData.address.state,
                  short_name: this.convertStateToAbbreviation(locationData.address.state),
                  types: ["administrative_area_level_1"]
                },
                {
                  long_name: locationData.address.zip,
                  short_name: locationData.address.zip,
                  types: ["postal_code"]
                }
              ]
            },
            availabilities: null,
          }

          // Call setLocation function
          if (window.bevmoLocation && typeof window.bevmoLocation.setLocation === "function") {
            try {
              await window.bevmoLocation.setLocation(locationObj)
              // Show success message with full address after location setup completes
              const fullAddress = locationData.address.address
              this.showLocationChangeSuccess(fullAddress)
            } catch (error) {
              console.error("Error setting location:", error)
              // Fallback: trigger location update
              this.switchToLocation(locationData)
            }
          } else {
            console.error("bevmoLocation.setLocation is not available")
            // Fallback: trigger location update
            this.switchToLocation(locationData)
          }
        } catch (error) {
          console.error("Error switching store:", error)
        }
      }

      switchToLocation(locationData) {
        // Update bevmoLocation with new location data
        if (window.bevmoLocation) {
          window.bevmoLocation.location_id = locationData.id
          window.bevmoLocation.fulfillment_type = "pickup"
          window.bevmoLocation.fulfillment_display_name = `${
            locationData.address.city
          }, ${this.convertStateToAbbreviation(locationData.address.state)}`
          
          // Convert API address format to bevmoLocation address format
          // API format: {address, city, state, zip}
          // bevmoLocation format: {address1, city, province, postalCode}
          window.bevmoLocation.address = {
            address1: locationData.address.address,
            city: locationData.address.city,
            province: locationData.address.state,
            postalCode: locationData.address.zip,
            latitude: locationData.address.latitude,
            longitude: locationData.address.longitude,
          }

          // Publish location update event
          publish(PUB_SUB_EVENTS.bevmoLocationUpdate, {
            location: window.bevmoLocation,
          })

          // Show success message with full address
          const fullAddress = locationData.address.address
          this.showLocationChangeSuccess(fullAddress)
        }
      }

      showLocationChangeSuccess(address) {
        // Show temporary success message when location is changed
        if (typeof toast === "function") {
          toast({
            message: `You're now shopping ${address}`,
            className: "success",
            settings: {
              duration: 4000,
              gravity: "top",
              position: "center",
              stopOnFocus: true,
            },
          })
        }
      }

      formatDistance(distanceKm) {
        // Convert kilometers to miles (1 km = 0.621371 miles)
        const distanceMiles = distanceKm * 0.621371

        if (distanceMiles < 0.1) {
          // For very short distances, show in feet (1 mile = 5280 feet)
          const distanceFeet = Math.round(distanceMiles * 5280)
          return `${distanceFeet}ft away`
        }

        return `${distanceMiles.toFixed(1)}mi away`
      }

      convertStateToAbbreviation(stateName) {
        // Map of full state names to their two-letter abbreviations
        const stateMap = {
          Alabama: "AL",
          Alaska: "AK",
          Arizona: "AZ",
          Arkansas: "AR",
          California: "CA",
          Colorado: "CO",
          Connecticut: "CT",
          Delaware: "DE",
          Florida: "FL",
          Georgia: "GA",
          Hawaii: "HI",
          Idaho: "ID",
          Illinois: "IL",
          Indiana: "IN",
          Iowa: "IA",
          Kansas: "KS",
          Kentucky: "KY",
          Louisiana: "LA",
          Maine: "ME",
          Maryland: "MD",
          Massachusetts: "MA",
          Michigan: "MI",
          Minnesota: "MN",
          Mississippi: "MS",
          Missouri: "MO",
          Montana: "MT",
          Nebraska: "NE",
          Nevada: "NV",
          "New Hampshire": "NH",
          "New Jersey": "NJ",
          "New Mexico": "NM",
          "New York": "NY",
          "North Carolina": "NC",
          "North Dakota": "ND",
          Ohio: "OH",
          Oklahoma: "OK",
          Oregon: "OR",
          Pennsylvania: "PA",
          "Rhode Island": "RI",
          "South Carolina": "SC",
          "South Dakota": "SD",
          Tennessee: "TN",
          Texas: "TX",
          Utah: "UT",
          Vermont: "VT",
          Virginia: "VA",
          Washington: "WA",
          "West Virginia": "WV",
          Wisconsin: "WI",
          Wyoming: "WY",
          "District of Columbia": "DC",
        }

        // Return the abbreviation if found, otherwise return the original state name
        return stateMap[stateName] || stateName
      }

      escapeHtml(text) {
        const div = document.createElement("div")
        div.textContent = text
        return div.innerHTML
      }

      getAllProductIds() {
        const productIds = new Set()

        if (this.dataset.gopuffProductId) {
          productIds.add(this.dataset.gopuffProductId.toString())
        }

        // Get all product IDs from cart items
        this.getCartProductIds().forEach((id) => productIds.add(id))

        // Convert Set to array and filter out empty values
        return Array.from(productIds).filter((id) => id && id.trim())
      }

      getCartProductIds() {
        const cartProductIds = []

        // Try to get from global cart data
        if (window.cart && window.cart.items) {
          window.cart.items.forEach((item) => {
            if (item.sku && !isNaN(item.sku)) {
              cartProductIds.push(item.sku.toString())
            }
          })
        }
        return cartProductIds
      }

      renderEmptyState() {
        this.locationsContainer.innerHTML = `
          <div class="nearby-locations__empty-state">
            <p>No nearby locations found.</p>
          </div>
        `
      }

      renderNoAvailabilityState() {
        this.locationsContainer.innerHTML = `
          <div class="nearby-locations__no-availability">
            <p>This product is not available at nearby locations.</p>
          </div>
        `
      }

      // Public method to update locations from external sources
      updateLocations(locations) {
        this.setLocations(locations)
      }
    }
  )
}
